import type { Id } from '@/infra/id'
import type { OfferExpirationAction } from '@/features/opportunities/offers/OpportunityOffer'

import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContentTitle,
  ModalSheetContentDescription,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSection,
  ModalSheetContentBox,
} from '@/components/ModalSheet'

import styles from './OfferModal.module.css'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { match } from '@/utils/pattern-match-util'
import cx from 'classnames'
import { formattedTimeUntil } from '@/utils/date-util'
import { ErrorBlock } from '@/components/ErrorBlock'
import { Congrats } from '@/features/opportunities/components/Congrats'

export interface OfferModalProps {
  id: Id
  showing: boolean
  workspaceName: string
  displayName: string
  championClientName: string
  proCut: string
  jobSpecs: string
  expirationAction: OfferExpirationAction
  timeUntilExpiration: number
  isAcceptPending: boolean
  onAccept: () => unknown
  acceptFailed: boolean
  acceptError?: unknown
  onDismissAcceptError: () => unknown
  isDeclinePending: boolean
  onDecline: () => unknown
  declineFailed: boolean
  declineError?: unknown
  onDismissDeclineError: () => unknown
}

/**
 * The Offer Modal.
 */
export function OfferModal({
  id,
  showing,
  workspaceName,
  displayName,
  championClientName,
  proCut,
  jobSpecs,
  expirationAction,
  timeUntilExpiration,
  isAcceptPending,
  onAccept,
  acceptFailed,
  acceptError,
  onDismissAcceptError,
  isDeclinePending,
  onDecline,
  declineFailed,
  declineError,
  onDismissDeclineError,
}: OfferModalProps) {
  const expirationActionText = match(expirationAction, {
    Accept: () => 'Auto-accepting',
    Decline: () => 'Auto-declining',
  })

  const clockStyles = cx(
    styles.clock,
    match(expirationAction, {
      Accept: () => styles.clockAutoAccept,
      Decline: () => styles.clockAutoDecline,
    }),
  )

  const countdownStyles = cx(
    styles.countdown,
    match(expirationAction, {
      Accept: () => styles.countdownAutoAccept,
      Decline: () => styles.countdownAutoDecline,
    }),
  )

  return (
    <ModalSheet showing={showing} disableCloseOnEsc disableCloseOnOutsideClick>
      <ModalSheetHeader />
      <ModalSheetContent leftAligned>
        <ModalSheetContentBlock>
          <Congrats />
          <ModalSheetContentTitle>
            You scored a job on {workspaceName}
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            To claim, review and accept the opportunity below.
          </ModalSheetContentDescription>
          <ModalSheetContentBox testId={`offer-modal-${id}`} color="green">
            <span
              className={styles.opportunityTitle}
              data-test={`offer-modal-display-name-${id}`}
            >
              {displayName}
            </span>
            <div className={styles.clientAndProCut}>
              <span>{championClientName}</span>
              {proCut && (
                <>
                  <span className={styles.separator}>•</span>
                  <span>{proCut}</span>
                </>
              )}
            </div>
            <p className={styles.specs}>{jobSpecs}</p>
            <div className={styles.offerAction}>
              <Button
                label="Accept Opportunity"
                onPress={onAccept}
                isPending={isAcceptPending}
                block
                testId={`offer-modal-accept-button-${id}`}
                variant="success"
                size="cta"
              />
            </div>
          </ModalSheetContentBox>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {acceptFailed && (
        <ErrorBlock
          tackOn="bottom"
          error={acceptError}
          onClose={onDismissAcceptError}
        />
      )}

      {declineFailed && (
        <ErrorBlock
          tackOn="bottom"
          error={declineError}
          onClose={onDismissDeclineError}
        />
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSection>
          <div className={styles.countdownDisplay}>
            <Icon className={clockStyles} name="clock" auto />
            {expirationActionText} in{' '}
            <span className={countdownStyles}>
              {formattedTimeUntil(timeUntilExpiration)}
            </span>
          </div>
        </ModalSheetFooterSection>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Decline Opportunity"
            onPress={onDecline}
            isPending={isDeclinePending}
            testId={`offer-modal-decline-button-${id}`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
